import React, { useEffect, useRef } from 'react'
import { format } from 'date-fns'
import type { WarEvent } from '../../types'
import styles from './LiveFeed.module.css'

export interface LiveFeedItem {
  id: string
  event: WarEvent
  receivedAt: Date
}

export interface LiveFeedProps {
  events: LiveFeedItem[]
  className?: string
}

/**
 * Format a WarEvent into a human-readable string without returning raw JSON.
 */
export function formatEvent(evt: WarEvent): string {
  if (!evt) return ''
  const payload = (evt.payload || {}) as Record<string, any>

  switch (evt.event_type) {
    case 'step.updated':
      return `${payload.team_slug || 'team'}: ${payload.step_name || 'step'} → ${payload.new_status || ''}`

    case 'step.unlocked':
      return `🔓 ${payload.step_name || 'step'} unlocked`

    case 'step.timeout':
      return `⏰ ${payload.step_name || 'step'} timeout!`

    case 'team.online':
      return `🟢 ${payload.team_slug || 'team'} online`

    case 'team.offline':
      return `🔴 ${payload.team_slug || 'team'} offline`

    case 'ai.alert': {
      const msg = typeof payload.message === 'string' ? payload.message : ''
      return `🤖 ${msg.length > 80 ? msg.substring(0, 80) + '…' : msg}`
    }

    case 'session.started':
      return '🚀 Session started'

    case 'session.completed':
      return '✅ Session completed'

    case 'gonogo.voted': {
      const isGo = payload.vote === 'go'
      const voteLabel = isGo ? 'GO' : payload.vote === 'no_go' ? 'NO-GO' : 'ABSTAIN'
      const icon = isGo ? '✅' : '❌'
      return `${icon} ${payload.team_slug || 'team'} voted ${voteLabel}`
    }

    default:
      return evt.event_type ? evt.event_type.replace('.', ': ') : 'unknown event'
  }
}

export function LiveFeed({ events, className }: LiveFeedProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef(events.length)

  // Auto-scroll to top (newest first) whenever new events arrive
  useEffect(() => {
    if (events.length > prevCountRef.current) {
      if (listRef.current) {
        listRef.current.scrollTop = 0
      }
    }
    prevCountRef.current = events.length
  }, [events.length])

  return (
    <aside className={`${styles.container} ${className || ''}`} aria-label="Live Activity Feed">
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.title}>LIVE FEED</span>
        </div>
        <span className={styles.count} aria-label={`${events.length} events`}>
          {events.length}
        </span>
      </div>

      <div className={styles.list} ref={listRef} data-testid="live-feed-list">
        {events.length === 0 ? (
          <div className={styles.empty}>No live activity yet</div>
        ) : (
          events.map((item) => {
            const dateObj = item.receivedAt instanceof Date ? item.receivedAt : new Date(item.receivedAt)
            const timeStr = format(dateObj, 'HH:mm:ss')
            const text = formatEvent(item.event)

            return (
              <div key={item.id} className={styles.item}>
                <span className={styles.timestamp}>[{timeStr}]</span>
                <span className={styles.text}>{text}</span>
              </div>
            )
          })
        )}
      </div>
    </aside>
  )
}
